import { faHistory } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatOperationModeName } from "src/shared/helpers/general-configuration.utils";
import bright_level_icon from "../../../../../../../assets/images/bright_level_icon.png";
import dim_level_icon from "../../../../../../../assets/images/dim_level_icon.png";
import hold_time_icon from "../../../../../../../assets/images/hold_time_icon.png";
import lighting_configuration_icon from "../../../../../../../assets/images/lighting-configuration-icon.png";
import operation_mode_icon from "../../../../../../../assets/images/operation-mode-icon.png";
import ramp_down_time_icon from "../../../../../../../assets/images/ramp_down_time_icon.png";
import ramp_up_time_icon from "../../../../../../../assets/images/ramp_up_time_icon.png";
import { GetConfigButton } from "../../components/get-config-button.component";
import type {
  AsyncStatus,
  LightingValues,
  OperationModeOption,
} from "../../components/types";
import { ConfigurationSliderCard } from "./configuration-cards.component";

type LightingConfigurationSectionProps = {
  operationModeOptions: OperationModeOption[];
  operationMode: string | null;
  onOperationModeChange: (mode: string) => void;
  lightingValues: LightingValues;
  onLightingChange: (field: keyof LightingValues, value: number | null) => void;
  onLightingReset: (field: keyof LightingValues) => void;
  gatewayConfigFetchStatus?: AsyncStatus;
  onGetGatewayConfig?: () => void;
  isLoadingCommandResultData?: boolean;
  onViewCommandResult?: () => void;
  onOpenAdvancedConfig?: () => void;
  errors?: Record<string, string>;
  gatewayConfigCountdown?: number;
  showGatewayConfigActions?: boolean;
  onViewHistory?: () => void;
  isUpdatedByZone?: boolean;
  updateStatusMessage?: string;
};

const LIGHTING_SLIDER_CONFIG = [
  {
    field: "rampUpTime" as const,
    title: "Ramp Up Time",
    subtitle: "Fade-in duration",
    suffix: "sec",
    min: 0,
    max: 120,
    icon: ramp_up_time_icon,
  },
  {
    field: "rampDownTime" as const,
    title: "Ramp Down Time",
    subtitle: "Fade-out duration",
    suffix: "sec",
    min: 0,
    max: 120,
    icon: ramp_down_time_icon,
  },
  {
    field: "dimLevel" as const,
    title: "Dim Level",
    subtitle: "Minimum brightness",
    suffix: "%",
    min: 0,
    max: 100,
    icon: dim_level_icon,
  },
  {
    field: "brightLevel" as const,
    title: "Bright Level",
    subtitle: "Maximum brightness",
    suffix: "%",
    min: 0,
    max: 100,
    icon: bright_level_icon,
  },
];

export function LightingConfigurationSection({
  operationModeOptions,
  operationMode,
  onOperationModeChange,
  lightingValues,
  onLightingChange,
  onLightingReset,
  gatewayConfigFetchStatus,
  onGetGatewayConfig,
  isLoadingCommandResultData,
  onViewCommandResult,
  onOpenAdvancedConfig,
  errors = {},
  gatewayConfigCountdown,
  showGatewayConfigActions = true,
  onViewHistory,
  isUpdatedByZone,
  updateStatusMessage,
}: LightingConfigurationSectionProps) {
  return (
    <section className="configuration-card--lighting configuration-card--large">
      <div className="configuration-card__header">
        <div>
          <div className="configuration-card__eyebrow h5">
            <img src={lighting_configuration_icon} alt="lighting-configuration-icon" className="me-2" />
            Lighting Configuration
          </div>
        </div>
        {(showGatewayConfigActions || onViewHistory) && (
          <div className="configuration-card__header-actions">
            {showGatewayConfigActions &&
              gatewayConfigFetchStatus != null &&
              onGetGatewayConfig &&
              onViewCommandResult &&
              isLoadingCommandResultData != null && (
                <GetConfigButton
                  status={gatewayConfigFetchStatus}
                  isLoading={isLoadingCommandResultData}
                  onGetConfig={onGetGatewayConfig}
                  onViewResult={onViewCommandResult}
                  label="Get Gateway Config"
                  countdown={gatewayConfigCountdown}
                  isUpdatedByZone={isUpdatedByZone}
                  updateStatusMessage={updateStatusMessage}
                />
              )}
            {onViewHistory && (
              <button
                type="button"
                className="configuration-icon-btn px-3 configuration-button"
                title="History"
                onClick={onViewHistory}
              >
                <FontAwesomeIcon icon={faHistory} />
                <span style={{ marginTop: "2px" }}>View History</span>
              </button>
            )}
          </div>
        )}
      </div>

      <div className="configuration-subcard">
        <div className="configuration-subcard__header">
          <div>
            <h3 className="configuration-subcard__title">
              <img src={operation_mode_icon} alt="Operation Mode icon" className="me-2" />
              Operation Mode
            </h3>
          </div>
          <span className={`${operationMode == undefined || operationMode == "null" ? "configuration-pill" : ""}`}>
            {operationMode == undefined || operationMode == "null" ? (
              "Not Selected"
            ) : (
              <>
                Current State:{" "}
                <span style={{ color: "#0b72cf", marginLeft: "0.35rem" }}>
                  {formatOperationModeName(operationMode)}
                </span>
              </>
            )}
          </span>
        </div>
        <div className="configuration-segmented">
          {operationModeOptions?.length > 0 ? (
            operationModeOptions?.map((mode) => (
              <button
                type="button"
                key={mode?.id}
                className={mode?.name === operationMode ? "is-active" : ""}
                onClick={() => onOperationModeChange(mode?.name)}
              >
                {formatOperationModeName(mode?.name)}
              </button>
            ))
          ) : (
            ["Auto", "Force On", "Force Off"].map((mode) => (
              <button
                type="button"
                key={mode}
                className={mode === operationMode ? "is-active" : ""}
                onClick={() => onOperationModeChange(mode)}
              >
                {mode}
              </button>
            ))
          )}
        </div>
      </div>

      <div className="configuration-mini-grid">
        {LIGHTING_SLIDER_CONFIG.map(
          ({ field, title, subtitle, suffix, min, max, icon }) => (
            <ConfigurationSliderCard
              key={field}
              title={title}
              subtitle={subtitle}
              suffix={suffix}
              value={lightingValues[field]}
              min={min}
              max={max}
              step={1}
              onValueChange={(value) => onLightingChange(field, value)}
              onClick={() => onLightingReset(field)}
              icon={icon}
              error={errors[field]}
            />
          ),
        )}
      </div>

      <div className="configuration-mini-grid configuration-mini-grid--single">
        <ConfigurationSliderCard
          title="Hold Time"
          subtitle="Safety timeout duration"
          suffix="sec"
          value={lightingValues.holdTime}
          min={0}
          max={180}
          step={1}
          onValueChange={(value) => onLightingChange("holdTime", value)}
          onClick={() => onLightingReset("holdTime")}
          icon={hold_time_icon}
          error={errors.holdTime}
        />
      </div>

      {/* <button
        type="button"
        className="configuration-advanced-toggle"
        onClick={onOpenAdvancedConfig}
      >
        <img src={advanced_configuration_icon} alt="advanced configuration icon" />
        <div style={{ marginTop: "4px" }}>Advanced Configuration</div>
      </button> */}
    </section>
  );
}
